import React from 'react';
import { 
  LayoutGrid, 
  Calculator, 
  Plus, 
  Calendar, 
  Users,
  Boxes,
  BarChart3,
  Store
} from 'lucide-react';

export type SpecialistTab = 'home' | 'orcamento' | 'agenda' | 'clientes' | 'materiais' | 'painel' | 'pdv';

interface SpecialistBottomNavProps {
  activeTab: SpecialistTab | null;
  onSelectTab: (tab: SpecialistTab) => void;
  onOpenQuickActions: () => void;
  plan?: string;
  isMasterUser?: boolean;
  isEnterpriseUser?: boolean;
  isSpecialistUser?: boolean;
  isKeyboardOpen?: boolean;
}

export default function SpecialistBottomNav({
  activeTab,
  onSelectTab,
  onOpenQuickActions,
  plan = 'Grátis',
  isMasterUser = false,
  isEnterpriseUser = false,
  isSpecialistUser = false,
  isKeyboardOpen = false
}: SpecialistBottomNavProps) {
  const isEnterpriseOrMaster = isMasterUser || isEnterpriseUser || ['Empresa', 'Empresarial', 'Enterprise'].includes(plan);
  const isSpecialistTier = !isEnterpriseOrMaster && (isSpecialistUser || ['Especialista', 'Premium'].includes(plan));
  const isBasicOrPro = !isEnterpriseOrMaster && !isSpecialistTier;

  return (
    <nav
      aria-label="Navegação Principal R-Max"
      className={`lg:hidden fixed bottom-0 left-0 right-0 z-40 w-full sm:w-auto sm:max-w-xl sm:mx-auto sm:bottom-4 sm:rounded-2xl sm:border sm:border-zinc-200/90 sm:shadow-xl bg-white/95 backdrop-blur-md border-t border-zinc-200/80 px-3 h-16 flex items-center justify-between transition-all duration-200 ${
        isKeyboardOpen ? 'translate-y-full opacity-0 pointer-events-none' : 'translate-y-0 opacity-100'
      }`}
      style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 4px)' }}
    >
      {/* 1. Início */}
      <button
        type="button"
        onClick={() => onSelectTab('home')}
        className="flex-1 flex flex-col items-center justify-center py-1 cursor-pointer transition-all active:scale-95 select-none"
        aria-current={activeTab === 'home' ? 'page' : undefined}
      >
        <div className={`px-3.5 py-1 rounded-full flex items-center justify-center transition-all ${
          activeTab === 'home'
            ? 'bg-amber-500/15 text-amber-800'
            : 'text-zinc-400 hover:text-zinc-700'
        }`}>
          <LayoutGrid className="h-5 w-5" strokeWidth={activeTab === 'home' ? 2.5 : 1.8} />
        </div>
        <span className={`text-[9px] mt-0.5 tracking-tight ${
          activeTab === 'home'
            ? 'font-black text-zinc-900'
            : 'font-semibold text-zinc-400'
        }`}>
          Início
        </span>
      </button>

      {/* 2. Orçar */}
      <button
        type="button"
        onClick={() => onSelectTab('orcamento')}
        className="flex-1 flex flex-col items-center justify-center py-1 cursor-pointer transition-all active:scale-95 select-none"
        aria-current={activeTab === 'orcamento' ? 'page' : undefined}
      >
        <div className={`px-3.5 py-1 rounded-full flex items-center justify-center transition-all ${
          activeTab === 'orcamento'
            ? 'bg-amber-500/15 text-amber-800'
            : 'text-zinc-400 hover:text-zinc-700'
        }`}>
          <Calculator className="h-5 w-5" strokeWidth={activeTab === 'orcamento' ? 2.5 : 1.8} />
        </div>
        <span className={`text-[9px] mt-0.5 tracking-tight ${
          activeTab === 'orcamento'
            ? 'font-black text-zinc-900'
            : 'font-semibold text-zinc-400'
        }`}>
          Orçar
        </span>
      </button>

      {/* 3. FAB Central M3 (+ Ações Rápidas) */}
      <div className="flex-1 flex flex-col items-center justify-center -mt-6">
        <button
          type="button"
          onClick={onOpenQuickActions}
          className="w-13 h-13 rounded-2xl bg-amber-500 hover:bg-amber-400 text-neutral-950 flex items-center justify-center shadow-lg shadow-amber-500/30 border-3 border-white active:scale-90 transition-all cursor-pointer group"
          title="Criar Novo"
          aria-label="Criar novo registro"
        >
          <Plus className="h-6 w-6 stroke-[3] group-hover:rotate-90 transition-transform duration-200" />
        </button>
        <span className="text-[8px] font-black uppercase tracking-wider text-amber-800 mt-1">
          Criar
        </span>
      </div>

      {/* 4. Quarta Aba (Dinâmica por Plano) */}
      {isBasicOrPro ? (
        <button
          type="button"
          onClick={() => onSelectTab('materiais')}
          className="flex-1 flex flex-col items-center justify-center py-1 cursor-pointer transition-all active:scale-95 select-none"
          aria-current={activeTab === 'materiais' ? 'page' : undefined}
        >
          <div className={`px-3.5 py-1 rounded-full flex items-center justify-center transition-all ${
            activeTab === 'materiais'
              ? 'bg-amber-500/15 text-amber-800'
              : 'text-zinc-400 hover:text-zinc-700'
          }`}>
            <Boxes className="h-5 w-5" strokeWidth={activeTab === 'materiais' ? 2.5 : 1.8} />
          </div>
          <span className={`text-[9px] mt-0.5 tracking-tight ${
            activeTab === 'materiais'
              ? 'font-black text-zinc-900'
              : 'font-semibold text-zinc-400'
          }`}>
            Materiais
          </span>
        </button>
      ) : isEnterpriseOrMaster ? (
        <button
          type="button"
          onClick={() => onSelectTab('pdv')}
          className="flex-1 flex flex-col items-center justify-center py-1 cursor-pointer transition-all active:scale-95 select-none"
          aria-current={activeTab === 'pdv' ? 'page' : undefined}
        >
          <div className={`px-3.5 py-1 rounded-full flex items-center justify-center transition-all ${
            activeTab === 'pdv'
              ? 'bg-amber-500/15 text-amber-800'
              : 'text-zinc-400 hover:text-zinc-700'
          }`}>
            <Store className="h-5 w-5" strokeWidth={activeTab === 'pdv' ? 2.5 : 1.8} />
          </div>
          <span className={`text-[9px] mt-0.5 tracking-tight ${
            activeTab === 'pdv'
              ? 'font-black text-zinc-900'
              : 'font-semibold text-zinc-400'
          }`}>
            Balcão
          </span>
        </button>
      ) : (
        <button
          type="button"
          onClick={() => onSelectTab('agenda')}
          className="flex-1 flex flex-col items-center justify-center py-1 cursor-pointer transition-all active:scale-95 select-none"
          aria-current={activeTab === 'agenda' ? 'page' : undefined}
        >
          <div className={`px-3.5 py-1 rounded-full flex items-center justify-center transition-all ${
            activeTab === 'agenda'
              ? 'bg-amber-500/15 text-amber-800'
              : 'text-zinc-400 hover:text-zinc-700'
          }`}>
            <Calendar className="h-5 w-5" strokeWidth={activeTab === 'agenda' ? 2.5 : 1.8} />
          </div>
          <span className={`text-[9px] mt-0.5 tracking-tight ${
            activeTab === 'agenda'
              ? 'font-black text-zinc-900'
              : 'font-semibold text-zinc-400'
          }`}>
            Agenda
          </span>
        </button>
      )}

      {/* 5. Quinta Aba (Dinâmica por Plano) */}
      {isBasicOrPro ? (
        <button
          type="button"
          onClick={() => onSelectTab('painel')}
          className="flex-1 flex flex-col items-center justify-center py-1 cursor-pointer transition-all active:scale-95 select-none"
          aria-current={activeTab === 'painel' ? 'page' : undefined}
        >
          <div className={`px-3.5 py-1 rounded-full flex items-center justify-center transition-all ${
            activeTab === 'painel'
              ? 'bg-amber-500/15 text-amber-800'
              : 'text-zinc-400 hover:text-zinc-700'
          }`}>
            <BarChart3 className="h-5 w-5" strokeWidth={activeTab === 'painel' ? 2.5 : 1.8} />
          </div>
          <span className={`text-[9px] mt-0.5 tracking-tight ${
            activeTab === 'painel'
              ? 'font-black text-zinc-900'
              : 'font-semibold text-zinc-400'
          }`}>
            Painel
          </span>
        </button>
      ) : (
        <button
          type="button"
          onClick={() => onSelectTab('clientes')}
          className="flex-1 flex flex-col items-center justify-center py-1 cursor-pointer transition-all active:scale-95 select-none"
          aria-current={activeTab === 'clientes' ? 'page' : undefined}
        >
          <div className={`px-3.5 py-1 rounded-full flex items-center justify-center transition-all ${
            activeTab === 'clientes'
              ? 'bg-amber-500/15 text-amber-800'
              : 'text-zinc-400 hover:text-zinc-700'
          }`}>
            <Users className="h-5 w-5" strokeWidth={activeTab === 'clientes' ? 2.5 : 1.8} />
          </div>
          <span className={`text-[9px] mt-0.5 tracking-tight ${
            activeTab === 'clientes'
              ? 'font-black text-zinc-900'
              : 'font-semibold text-zinc-400'
          }`}>
            Clientes
          </span>
        </button>
      )}
    </nav>
  );
}
